const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

const text = (value) => (typeof value === "string" ? value : "");

export const LIST_TONES = ["green", "amber", "red", "neutral"];

// List entries are plain strings or { text, tone }; an unknown tone renders as plain text.
const normalizeListEntry = (entry) => {
  if (typeof entry === "string") return entry;
  if (!entry || typeof entry !== "object" || typeof entry.text !== "string") return null;
  return LIST_TONES.includes(entry.tone) ? { text: entry.text, tone: entry.tone } : entry.text;
};

// Keeps only the block shapes AnnouncementModal knows how to render.
export const normalizeContentBlock = (block) => {
  if (typeof block === "string") return block;
  if (!block || typeof block !== "object") return null;

  if (Array.isArray(block.list)) {
    const list = block.list.map(normalizeListEntry).filter((entry) => entry !== null);
    return list.length ? { list } : null;
  }

  if (typeof block.text !== "string") return null;

  if (block.href !== undefined) {
    if (typeof block.href !== "string" || !SAFE_HREF.test(block.href.trim())) {
      return { text: block.text, strong: block.strong === true };
    }
    return {
      text: block.text,
      href: block.href.trim(),
      ...(typeof block.label === "string" ? { label: block.label } : {}),
      ...(typeof block.suffix === "string" ? { suffix: block.suffix } : {}),
      external: block.external === true,
    };
  }

  return { text: block.text, strong: block.strong === true };
};

export const normalizeNotification = (value) => {
  if (!value || typeof value !== "object") return null;
  const id = value.id === undefined || value.id === null ? "" : String(value.id);
  const title = text(value.title).trim();
  if (!id || !title) return null;

  const contentBlocks = Array.isArray(value.contentBlocks)
    ? value.contentBlocks.map(normalizeContentBlock).filter(Boolean)
    : [];

  return {
    id,
    key: text(value.key),
    version: value.version ?? null,
    severity: text(value.severity),
    priority: Number.isFinite(value.priority) ? value.priority : 0,
    publishedAt: text(value.publishedAt) || null,
    title,
    contentBlocks,
    acknowledgeLabel: text(value.acknowledgeLabel).trim() || null,
    doNotShowAgainLabel: text(value.doNotShowAgainLabel).trim() || null,
    closeLabel: text(value.closeLabel).trim() || null,
    read: value.read === true,
    dismissed: value.dismissed === true,
    showOnLogin: value.showOnLogin === true,
  };
};

export const normalizeNotificationsResponse = (value) => {
  const source = value && typeof value === "object" ? value : {};
  const list = Array.isArray(source.notifications) ? source.notifications : [];
  return list.map(normalizeNotification).filter(Boolean);
};

export const getLoginNotifications = (notifications) =>
  notifications.filter((item) => item.showOnLogin && !item.dismissed);

export const countUnread = (notifications) =>
  notifications.reduce((count, item) => count + (item.read ? 0 : 1), 0);

export const toAnnouncement = (notification) => ({
  title: notification.title,
  body: notification.contentBlocks,
});

export const formatNotificationDate = (publishedAt, language) => {
  if (!publishedAt) return "";
  const date = new Date(publishedAt);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(language === "en" ? "en-GB" : "pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
